import React, { useMemo, useState } from 'react';
import {
  App,
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Typography,
} from 'antd';
import { ArrowLeftOutlined, PrinterOutlined, SearchOutlined } from '@ant-design/icons';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import cspmService from '@/services/cspm.service';
import { useActiveCustomers } from '@/hooks/useCustomers';
import { useCspmPermissions } from '@/hooks/useCspmPermissions';
import { CspmSourceDocumentSelect } from '@/components/cspm/CspmSourceDocumentSelect';
import {
  CSPM_SOURCE_TYPES,
  CSPM_SOURCE_TYPE_LABELS,
  enumOptions,
  type CustomerPropertyReconciliation,
  type ReconciliationPreview,
} from '@/constants/cspm';
import { EngineCspmReconciliationPrint } from '@/components/print/EngineLetterheadPrints';

const { Text } = Typography;

const CustomerPropertyReconciliationPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { message, modal } = App.useApp();
  const [form] = Form.useForm();
  const cspmPerms = useCspmPermissions();
  const [previewParams, setPreviewParams] = useState<{
    source_document_type: string;
    source_document_id: string;
    customer_id?: string;
  } | null>(null);
  const [printOpen, setPrintOpen] = useState(false);
  const [closed, setClosed] = useState<CustomerPropertyReconciliation | null>(
    (location.state as { reconciliation?: CustomerPropertyReconciliation } | null)?.reconciliation ??
      null
  );

  const watchedSourceType = Form.useWatch('source_document_type', form);
  const watchedSourceId = Form.useWatch('source_document_id', form);
  const watchedCustomerId = Form.useWatch('customer_id', form);

  const { data: customers = [] } = useActiveCustomers();
  const customerOptions = useMemo(
    () =>
      (customers as Array<{ id: string; customer_name?: string; name?: string }>).map((c) => ({
        value: c.id,
        label: c.customer_name || c.name || c.id,
      })),
    [customers]
  );

  const isExistingId = Boolean(id && id !== 'new');

  const { data: loadedRecon, isLoading: loadingRecon } = useQuery({
    queryKey: ['cspm', 'reconciliation', id],
    queryFn: () => cspmService.getReconciliation(id!),
    enabled: isExistingId,
  });

  React.useEffect(() => {
    if (!loadedRecon) return;
    setClosed(loadedRecon);
    form.setFieldsValue({
      source_document_type: loadedRecon.source_document_type,
      source_document_id: loadedRecon.source_document_id,
      customer_id: loadedRecon.customer_id,
      variance_approved: loadedRecon.variance_approved,
      variance_reason: loadedRecon.variance_reason,
    });
  }, [loadedRecon, form]);

  const { data: preview, isFetching } = useQuery({
    queryKey: ['cspm', 'recon-preview', previewParams],
    queryFn: () => cspmService.previewReconciliation(previewParams!),
    enabled: Boolean(previewParams?.source_document_type && previewParams?.source_document_id),
  });

  const { data: closure, isFetching: loadingClosure } = useQuery({
    queryKey: ['cspm', 'closure', watchedSourceType, watchedSourceId],
    queryFn: () =>
      cspmService.checkClosure({
        source_document_type: watchedSourceType,
        source_document_id: watchedSourceId,
      }),
    enabled: Boolean(watchedSourceType && watchedSourceId),
  });

  const closeMut = useMutation({
    mutationFn: (body: {
      source_document_type: string;
      source_document_id: string;
      customer_id?: string;
      variance_approved?: boolean;
      variance_reason?: string | null;
    }) => cspmService.createReconciliation(body),
    onSuccess: (row) => {
      message.success('Reconciliation closed');
      if (row) {
        setClosed(row);
        navigate(`/cspm/reconciliations/${row.id}`, {
          replace: true,
          state: { reconciliation: row },
        });
      }
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Close failed'),
  });

  const onPreview = async () => {
    const values = await form.validateFields(['source_document_type', 'source_document_id']);
    setPreviewParams({
      source_document_type: values.source_document_type,
      source_document_id: values.source_document_id,
      customer_id: form.getFieldValue('customer_id') || undefined,
    });
    setClosed(null);
  };

  const onClose = async () => {
    const values = await form.validateFields();
    const unresolved = Number(preview?.unresolved_qty || 0);
    const varianceApproved = Boolean(values.variance_approved);
    if (unresolved > 0 && !varianceApproved) {
      message.error('Unresolved qty requires variance approval to close');
      return;
    }
    modal.confirm({
      title: 'Close reconciliation for this source?',
      content:
        unresolved > 0
          ? `Unresolved qty ${unresolved} will be closed with approved variance.`
          : 'Balances will be marked closed for this source.',
      onOk: () =>
        closeMut.mutateAsync({
          source_document_type: values.source_document_type,
          source_document_id: values.source_document_id,
          customer_id: values.customer_id || undefined,
          variance_approved: varianceApproved,
          variance_reason: values.variance_reason || null,
        }),
    });
  };

  const customerName =
    customerOptions.find(
      (c) => c.value === (closed?.customer_id || preview?.customer_id || form.getFieldValue('customer_id'))
    )?.label || 'Customer';

  const printData: CustomerPropertyReconciliation | ReconciliationPreview | null =
    closed || preview || null;

  const snapshot =
    closed?.snapshot_json && typeof closed.snapshot_json === 'object'
      ? (closed.snapshot_json as ReconciliationPreview)
      : preview;

  if (isExistingId && loadingRecon && !closed) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/cspm')}>
                Back
              </Button>
              <div>
                <h2 style={{ margin: 0 }}>Externally owned property reconciliation</h2>
                <Text type="secondary">
                  {id && closed
                    ? closed.reconciliation_number
                    : 'Preview balances for a commercial source, then close'}
                </Text>
              </div>
            </Space>
          </Col>
          <Col>
            <Space>
              {printData && (
                <Button icon={<PrinterOutlined />} onClick={() => setPrintOpen(true)}>
                  Print
                </Button>
              )}
            </Space>
          </Col>
        </Row>

        <Card title="Source">
          <Form form={form} layout="vertical" disabled={Boolean(closed)}>
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item
                  name="source_document_type"
                  label="Source type"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <Select
                    options={enumOptions(CSPM_SOURCE_TYPES, CSPM_SOURCE_TYPE_LABELS)}
                    onChange={() => form.setFieldsValue({ source_document_id: undefined })}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="source_document_id"
                  label="Source document"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <CspmSourceDocumentSelect
                    sourceType={watchedSourceType}
                    customerId={watchedCustomerId}
                    onValidated={(result) => {
                      if (result?.customer_id) {
                        form.setFieldsValue({ customer_id: result.customer_id });
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="customer_id" label="Customer (optional)">
                  <Select allowClear showSearch optionFilterProp="label" options={customerOptions} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="variance_approved" valuePropName="checked">
                  <Checkbox>Approve unresolved variance</Checkbox>
                </Form.Item>
              </Col>
              <Col xs={24} md={16}>
                <Form.Item name="variance_reason" label="Variance reason">
                  <Input placeholder="Required when approving unresolved qty" />
                </Form.Item>
              </Col>
            </Row>
            {!closed && cspmPerms.canReconcile && (
              <Space>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  loading={isFetching}
                  onClick={onPreview}
                >
                  Preview
                </Button>
                <Button
                  disabled={!preview}
                  loading={closeMut.isPending}
                  onClick={onClose}
                >
                  Close reconciliation
                </Button>
              </Space>
            )}
            {!closed && !cspmPerms.canReconcile && (
              <Text type="secondary">You do not have permission to reconcile externally owned property.</Text>
            )}
          </Form>
        </Card>

        {closure && (
          <Alert
            type={
              closure.has_unresolved
                ? closure.policy === 'BLOCK'
                  ? 'error'
                  : 'warning'
                : 'success'
            }
            showIcon
            message={
              closure.has_unresolved
                ? `Closure gate (${closure.policy}): unresolved externally owned property`
                : `Closure gate (${closure.policy}): clear`
            }
            description={
              loadingClosure
                ? 'Checking…'
                : [
                    ...(closure.messages || []),
                    `Open receipts: ${closure.open_receipts ?? 0}`,
                    `Open exceptions: ${closure.open_exceptions ?? 0}`,
                    `Open allocations: ${closure.open_allocations ?? 0}`,
                    `Customer stock on hand: ${closure.customer_stock_on_hand ?? 0}`,
                    closure.policy === 'APPROVAL_REQUIRED' && closure.has_unresolved
                      ? 'Commercial close (SO / Opp / PR / CR / EPC) requires cspm_closure_override=true — audited when used'
                      : null,
                    closure.policy === 'BLOCK' && closure.has_unresolved
                      ? 'BLOCK policy: resolve custody before commercial close (override not allowed)'
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')
            }
          />
        )}

        {(snapshot || closed) && (
          <Card title={closed ? 'Closed snapshot' : 'Preview balances'}>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col xs={12} md={4}>
                <Statistic title="Expected" value={snapshot?.expected_qty ?? 0} />
              </Col>
              <Col xs={12} md={4}>
                <Statistic title="Received" value={snapshot?.received_qty ?? 0} />
              </Col>
              <Col xs={12} md={4}>
                <Statistic title="Accepted" value={snapshot?.accepted_qty ?? 0} />
              </Col>
              <Col xs={12} md={4}>
                <Statistic title="Allocated" value={snapshot?.allocated_qty ?? 0} />
              </Col>
              <Col xs={12} md={4}>
                <Statistic title="Returned" value={snapshot?.returned_qty ?? 0} />
              </Col>
              <Col xs={12} md={4}>
                <Statistic
                  title="Unresolved"
                  value={snapshot?.unresolved_qty ?? closed?.unresolved_qty ?? 0}
                  valueStyle={{
                    color: Number(snapshot?.unresolved_qty || 0) > 0 ? '#cf1322' : undefined,
                  }}
                />
              </Col>
            </Row>
            <Table
              rowKey={(_, idx) => String(idx)}
              dataSource={snapshot?.lines || []}
              pagination={false}
              columns={[
                { title: 'Description', dataIndex: 'description' },
                { title: 'Expected', dataIndex: 'expected_qty', width: 100 },
                { title: 'Received', dataIndex: 'received_qty', width: 100 },
                { title: 'Accepted', dataIndex: 'accepted_qty', width: 100 },
                { title: 'Allocated', dataIndex: 'allocated_qty', width: 100 },
                { title: 'Returned', dataIndex: 'returned_qty', width: 100 },
                { title: 'Remaining', dataIndex: 'remaining_qty', width: 100 },
              ]}
            />
          </Card>
        )}

        {cspmPerms.canAdvisoryView && watchedCustomerId ? (
          <RelatedFocEvidenceCard customerId={watchedCustomerId} />
        ) : null}
      </Space>

      <Modal
        open={printOpen}
        onCancel={() => setPrintOpen(false)}
        footer={null}
        width="90vw"
        style={{ top: 24 }}
        destroyOnClose
      >
        {printData && (
          <EngineCspmReconciliationPrint
            data={printData}
            customerName={customerName}
            documentNumber={closed?.reconciliation_number}
            onClose={() => setPrintOpen(false)}
          />
        )}
      </Modal>
    </div>
  );
};

export default CustomerPropertyReconciliationPage;

function RelatedFocEvidenceCard({ customerId }: { customerId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['cspm', 'foc-cpr-advisory', customerId],
    queryFn: () => cspmService.getFocCprAdvisory({ customer_id: customerId }),
    enabled: Boolean(customerId),
  });
  return (
    <Card title="Related FOC evidence" loading={isLoading}>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        {data?.note || 'Advisory FOC↔EOP matches for this customer — no qty merge.'}
      </Text>
      <Table
        rowKey={(r) => String(r.match_key)}
        dataSource={data?.matches || []}
        pagination={{ pageSize: 10 }}
        locale={{ emptyText: 'No FOC↔EOP matches' }}
        columns={[
          {
            title: 'Match on',
            dataIndex: 'match_on',
            width: 140,
            render: (v: string[]) => (v || []).join(', ') || '—',
          },
          { title: 'Heat', dataIndex: 'heat_number', width: 120, render: (v) => v || '—' },
          { title: 'Serial', dataIndex: 'serial_number', width: 120, render: (v) => v || '—' },
          {
            title: 'EOP',
            key: 'cpr',
            render: (_: unknown, row: Record<string, any>) =>
              row.cpr?.receipt_number || row.cpr?.receipt_id || '—',
          },
          {
            title: 'FOC voucher',
            key: 'foc',
            render: (_: unknown, row: Record<string, any>) =>
              row.foc?.voucher_number || row.foc?.voucher_id || '—',
          },
        ]}
      />
    </Card>
  );
}
